import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Preloaded namespaces (small, shared, needed early)
import enCommon from './locales/en/common.json'
import frCommon from './locales/fr/common.json'
import enNav from './locales/en/nav.json'
import frNav from './locales/fr/nav.json'
import enAuth from './locales/en/auth.json'
import frAuth from './locales/fr/auth.json'
import enErrors from './locales/en/errors.json'
import frErrors from './locales/fr/errors.json'
import enHome from './locales/en/home.json'
import frHome from './locales/fr/home.json'
import enMandates from './locales/en/mandates.json'
import frMandates from './locales/fr/mandates.json'
import enEntities from './locales/en/entities.json'
import frEntities from './locales/fr/entities.json'
import enBanks from './locales/en/banks.json'
import frBanks from './locales/fr/banks.json'
import enBankAccounts from './locales/en/bankAccounts.json'
import frBankAccounts from './locales/fr/bankAccounts.json'
import enSignatories from './locales/en/signatories.json'
import frSignatories from './locales/fr/signatories.json'
import enUsers from './locales/en/users.json'
import frUsers from './locales/fr/users.json'
import enEbam from './locales/en/ebam.json'
import frEbam from './locales/fr/ebam.json'
import enWorkflows from './locales/en/workflows.json'
import frWorkflows from './locales/fr/workflows.json'
import enReviews from './locales/en/reviews.json'
import frReviews from './locales/fr/reviews.json'
import enContact from './locales/en/contact.json'
import frContact from './locales/fr/contact.json'
import enLegal from './locales/en/legal.json'
import frLegal from './locales/fr/legal.json'
import enContactPage from './locales/en/contactPage.json'
import frContactPage from './locales/fr/contactPage.json'
import enPrivacy from './locales/en/privacy.json'
import frPrivacy from './locales/fr/privacy.json'
import enTerms from './locales/en/terms.json'
import frTerms from './locales/fr/terms.json'
import enSettings from './locales/en/settings.json'
import frSettings from './locales/fr/settings.json'
import enHelp from './locales/en/help.json'
import frHelp from './locales/fr/help.json'
import enKyc from './locales/en/kyc.json'
import frKyc from './locales/fr/kyc.json'

export const SUPPORTED_LANGS = ['fr', 'en'] as const
export type SupportedLang = (typeof SUPPORTED_LANGS)[number]
export const DEFAULT_LANG: SupportedLang = 'fr'

export function isSupportedLang(lang: string): lang is SupportedLang {
  return SUPPORTED_LANGS.includes(lang as SupportedLang)
}

// Lazy namespaces loaded on demand via loadNamespace()
const LAZY_NAMESPACES = [] as const
export type LazyNamespace = (typeof LAZY_NAMESPACES)[number]

const PRELOADED_NAMESPACES = [
  'common',
  'nav',
  'auth',
  'errors',
  'home',
  'mandates',
  'entities',
  'banks',
  'bankAccounts',
  'signatories',
  'users',
  'ebam',
  'workflows',
  'reviews',
  'contact',
  'legal',
  'contactPage',
  'privacy',
  'terms',
  'settings',
  'help',
  'kyc',
] as const

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        nav: enNav,
        auth: enAuth,
        errors: enErrors,
        home: enHome,
        mandates: enMandates,
        entities: enEntities,
        banks: enBanks,
        bankAccounts: enBankAccounts,
        signatories: enSignatories,
        users: enUsers,
        ebam: enEbam,
        workflows: enWorkflows,
        reviews: enReviews,
        contact: enContact,
        legal: enLegal,
        contactPage: enContactPage,
        privacy: enPrivacy,
        terms: enTerms,
        settings: enSettings,
        help: enHelp,
        kyc: enKyc,
      },
      fr: {
        common: frCommon,
        nav: frNav,
        auth: frAuth,
        errors: frErrors,
        home: frHome,
        mandates: frMandates,
        entities: frEntities,
        banks: frBanks,
        bankAccounts: frBankAccounts,
        signatories: frSignatories,
        users: frUsers,
        ebam: frEbam,
        workflows: frWorkflows,
        reviews: frReviews,
        contact: frContact,
        legal: frLegal,
        contactPage: frContactPage,
        privacy: frPrivacy,
        terms: frTerms,
        settings: frSettings,
        help: frHelp,
        kyc: frKyc,
      },
    },
    defaultNS: 'common',
    ns: [...PRELOADED_NAMESPACES],
    fallbackLng: DEFAULT_LANG,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
  })

const loaded = new Set<string>()

export async function loadNamespace(ns: LazyNamespace, lang?: SupportedLang): Promise<void> {
  const target = lang ?? (i18n.language as SupportedLang)
  const effective = isSupportedLang(target) ? target : DEFAULT_LANG
  const key = `${effective}:${ns}`
  if (loaded.has(key)) return

  const bundle = await import(`./locales/${effective}/${ns}.json`)
  i18n.addResourceBundle(effective, ns, bundle.default ?? bundle, true, true)
  loaded.add(key)
}

export default i18n
